import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchHouseAccounts,
  createHouseAccount,
  fetchHouseAccountItems,
  postHouseAccountItem,
  type ListHouseAccountsQuery,
  type CreateHouseAccountPayload,
  type PostHouseAccountItemPayload,
} from '@/services/api/houseAccounts.api';

export function useHouseAccounts(query: ListHouseAccountsQuery, enabled = true) {
  return useQuery({
    queryKey: ['house-accounts', query],
    queryFn: () => fetchHouseAccounts(query),
    staleTime: 30_000,
    enabled: enabled && !!query.propertyId,
  });
}

export function useHouseAccountItems(id: string | null) {
  return useQuery({
    queryKey: ['house-accounts', id, 'items'],
    queryFn: () => fetchHouseAccountItems(id as string),
    staleTime: 30_000,
    enabled: id !== null,
  });
}

export function useCreateHouseAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateHouseAccountPayload) => createHouseAccount(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['house-accounts'] });
    },
  });
}

export function usePostHouseAccountItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: PostHouseAccountItemPayload }) =>
      postHouseAccountItem(id, payload),
    onSuccess: (_data, { id }) => {
      // Refresh the line items AND the master list (cached balance changed).
      void queryClient.invalidateQueries({ queryKey: ['house-accounts', id, 'items'] });
      void queryClient.invalidateQueries({ queryKey: ['house-accounts'] });
    },
  });
}
